import { useMemo, useState } from 'react';
import {
  Control,
  ListItemControlItems,
  StaticControlItem,
} from '../../../../types/types';
import { useControlApiMethodItems } from '../../../../hooks/useControlApiMethodItems';
import { shouldFetchEagerly } from '../utils/shouldFetchEagerly';
import { isApiMethodItems } from '../../../../utils/isApiMethodItems';
import { isListItemControlItems } from '../../../../utils/isListItemControlItems';
import { resolveListItemOptions } from '../../../../utils/resolveListItemOptions';
import { useDatasetFormDataOptional } from '../../../../hooks/context/useDatasetFormData';

interface GridInputOptionsResult {
  options: StaticControlItem[];
  isLoading: boolean;
  handlePopoverShow: () => void;
}

// Resolves options and loading state for list-based controls (multiCheckbox, radioBtn, combobox).
// Defers dynamic fetch for popover controls until first open, unless eager fetch is needed for label lookup.
// For dynamic radioBtn/combobox inside DatasetForm: skips own fetch and uses form-level prefetch result instead.
// For the listItem variant: reads the option list from the form GET/DEFAULT response, no request at all.
export const useGridInputOptions = (
  control: Control | undefined,
  value: unknown,
  name: string
): GridInputOptionsResult => {
  const formCtx = useDatasetFormDataOptional();

  // Extract items only for controls that use option lists
  const controlItems =
    control?.type === 'multiCheckbox' ||
    control?.type === 'radioBtn' ||
    control?.type === 'combobox'
      ? control.items
      : undefined;

  // Track first popover open to gate lazy fetch for multiCheckbox/combobox
  const [hasOpenedPopover, setHasOpenedPopover] = useState<boolean>(false);

  const isPopoverControl =
    control?.type === 'multiCheckbox' || control?.type === 'combobox';

  // Eager fetch when a stored value needs label resolution before the popover is opened
  const needsEagerFetch = useMemo<boolean>(
    () =>
      shouldFetchEagerly(isPopoverControl, controlItems, value, control?.type),
    [isPopoverControl, controlItems, value, control?.type]
  );

  const listItemControlItems: ListItemControlItems | null =
    controlItems !== undefined && isListItemControlItems(controlItems)
      ? controlItems
      : null;

  const formData: Record<string, unknown> | null = formCtx?.formData ?? null;
  // The listItem variant names a property of the form GET/DEFAULT response that holds the option list
  const rawListItemValue: unknown = listItemControlItems
    ? formData?.[listItemControlItems.listItem]
    : undefined;

  const listItemOptions = useMemo<StaticControlItem[]>(() => {
    if (!listItemControlItems) return [];
    return resolveListItemOptions(listItemControlItems, rawListItemValue);
  }, [listItemControlItems, rawListItemValue]);

  // Determine whether this control's options come from the form-level prefetch.
  // Only applies to dynamic (apiMethod) radioBtn/combobox inside DatasetForm.
  const isApiMethodListControl =
    (control?.type === 'radioBtn' || control?.type === 'combobox') &&
    controlItems !== undefined &&
    isApiMethodItems(controlItems);

  const prefetchedOptionsMap = formCtx?.prefetchedFormOptions ?? null;
  // null = prefetch still in flight; Record = prefetch settled
  const isPrefetchInProgress =
    isApiMethodListControl && prefetchedOptionsMap === null;
  const prefetched =
    isApiMethodListControl && prefetchedOptionsMap !== null
      ? prefetchedOptionsMap[name] // undefined if field not in map (e.g. fetch failed)
      : undefined;
  const hasPrefetched = prefetched !== undefined;

  // Bypass own fetch when prefetch handles this field; otherwise fall back to popover/eager logic.
  let enabled = true;
  if (isApiMethodListControl && (isPrefetchInProgress || hasPrefetched)) {
    enabled = false;
  } else if (isPopoverControl) {
    enabled = hasOpenedPopover || needsEagerFetch;
  }

  const { options: fetchedOptions, isLoading: fetchLoading } =
    useControlApiMethodItems(controlItems, enabled);

  const handlePopoverShow = (): void => setHasOpenedPopover(true);

  // For prefetch-backed controls: show spinner while prefetch runs, then use its result
  const remoteOptions = hasPrefetched ? prefetched : fetchedOptions;
  const options = listItemControlItems ? listItemOptions : remoteOptions;

  // multiCheckbox value from API is always string splitted by ","
  // so option values have to be stringified to prevent bugs when comparing number to string
  if (control?.type === 'multiCheckbox') {
    options.forEach((item) => {
      item.value = String(item.value);
    });
  }

  const isLoading = isPrefetchInProgress
    ? true
    : hasPrefetched
      ? false
      : fetchLoading;

  return { options, isLoading, handlePopoverShow };
};
